import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import * as actions from 'Actions';
import ImportWizardHeader from './ImportWizardHeader';
import classnames from 'classnames';
import {
  EditCircle,
  CancelCircle,
  CheckOnCircle,
  Filter as FilterIcon,
} from 'Utils/SvgIcons';
import service from 'Api/service';
import { saveError } from 'Api';
import {
  buildFromJson,
  getCellErrors,
  rowLevelErrors,
  followImportProgress,
} from 'HelperFunctions/import_wizard';
import PaginationBoxView from 'Utils/PaginationBoxView';
import Dropdown from 'Utils/Dropdown';
import ImportWizardAdjustFilterDrawer from './ImportWizardAdjustFilterDrawer';
import { history } from 'Components/utils/router/history';
import Modal from 'Utils/Modal';
import LoadingSpinner from 'Utils/LoadingSpinner';

const API = import.meta.env.VITE_API_DOMAIN;

// Adjust step: the staged grid. Every row's problems are listed on the row ("Column: message"),
// unrecognised columns are named and ignorable in one click, and column changes re-validate the
// whole batch in the background before the grid reloads.
class ImportWizardAdjust extends Component {
  state = {
    requiredColumns: [],
    columnOptions: {},
    userColumns: [],
    unmatchedColumns: [],
    ignoredColumns: [],
    editValues: {},
    tableData: [],
    displayColumns: [],
    errorCount: 0,
    loading: false,
    page: 1,
    perPage: 25,
    totalPages: 1,
    totalRows: 0,
    isFilterDrawerOpen: false,
    filterData: { filterDuplicates: '', filterProblems: '' },
    searchQuery: '',
    deleteColumnConfirmation: false,
    openCancelConfirmation: false,
    lastRowDeleteConfirmation: false,
  };

  componentDidMount() {
    const { dataType } = this.props.match.params;
    this.setState({ dataType }, () => this.getOnGoingImport());
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.business &&
      !prevProps.business.permissions.importWizardEnabled
    ) {
      history.push('/unauthorized');
    }
  }

  getOnGoingImport = () => {
    this.setState({ loading: true });
    const {
      page,
      perPage,
      dataType,
      filterData,
      searchQuery = '',
    } = this.state;
    service
      .get(
        `${API}/api/import_data?import_type=${dataType}&page=${page}&per=${perPage}&has_errors=${filterData.filterProblems}&has_duplicated=${filterData.filterDuplicates}&query=${encodeURIComponent(searchQuery)}`
      )
      .then((response) => {
        const { data } = response;
        const displayColumns = data.columns;
        this.setState({
          tableData: data.data.map(buildFromJson),
          errorCount: data.error_count,
          requiredColumns: data.expected_columns,
          columnOptions: data.options || {},
          userColumns: data.columns,
          unmatchedColumns: data.unmatched_columns || [],
          ignoredColumns: data.ignored_columns || [],
          importId: data.import,
          displayColumns,
          loading: false,
          totalPages: data.meta.pagination.total_pages,
          totalRows: data.meta.pagination.total_objects,
        });
      })
      .catch((error) => {
        saveError(error);
        this.setState({ loading: false });
      });
  };

  // Column operations answer 202 with the batch id; the grid reloads once the re-validation job finishes.
  waitForRevalidation = (request, label) => {
    const { openLoadingSpinner, closeLoadingSpinner, setErrors } = this.props;
    openLoadingSpinner(label);
    return request
      .then((response) => followImportProgress(response.data.import))
      .then(() => {
        closeLoadingSpinner();
        this.getOnGoingImport();
        return true;
      })
      .catch((error) => {
        closeLoadingSpinner();
        setErrors(
          error?.response?.data?.error ||
            error?.response?.message ||
            'The change could not be applied.'
        );
        saveError(error);
        this.setState({ loading: false });
        return false;
      });
  };

  setCellEdit = (rowIndex, columnName) => {
    const { tableData, editValues } = this.state;
    tableData[rowIndex].isEditing = columnName;
    editValues[rowIndex] = tableData[rowIndex][columnName];
    this.setState({ tableData, editValues });
    document.getElementById(`${rowIndex}${columnName}`).focus();
  };

  cancelCellEdit = (rowIndex, columnName) => {
    const { tableData, editValues } = this.state;
    tableData[rowIndex][columnName] = editValues[rowIndex];
    tableData[rowIndex].isEditing = false;
    this.setState({ tableData });
  };

  handleCellAdjust = (cellData, rowIndex, columnName) => {
    const { dataType } = this.state;
    service
      .patch(`${API}/api/import_data/${cellData.id}?import_type=${dataType}`, {
        column_name: columnName,
        column_data: cellData[columnName] ?? '',
      })
      .then(() => {
        // Names and emails affect duplicate checks on other rows: reload the page so every row is current.
        this.getOnGoingImport();
      })
      .catch((error) => {
        saveError(error);
        this.setState({ loading: false });
      });
  };

  updateCellValue = (event, rowIndex, columnName) => {
    const { tableData } = this.state;
    tableData[rowIndex][columnName] = event.target.value;
    this.setState({ tableData });
  };

  // Closed lists (booleans, units, Item Type) edit as a dropdown; colours, which can hold several names,
  // as a text box with suggestions (Release 38 review).
  cellInput = (data, rowIndex, columnName, editing, error) => {
    const options = this.state.columnOptions[columnName];
    const props = {
      className: classnames({
        inputProblemWarning: error,
        importWizardTableInput: true,
      }),
      key: `${rowIndex}${columnName}`,
      id: `${rowIndex}${columnName}`,
      value: data ?? '',
      disabled: !editing,
      onChange: (e) => this.updateCellValue(e, rowIndex, columnName),
    };
    if (editing && options && !options.multiple) {
      const values = [...new Set(['', props.value, ...options.values])];
      return (
        <select {...props}>
          {values.map((value) => (
            <option key={value} value={value}>
              {value === '' ? '(blank)' : value}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        {...props}
        type='text'
        list={options?.multiple ? `options-${columnName}` : undefined}
      />
    );
  };

  adjustCell = (
    data,
    rowIndex,
    columnName,
    isEditing = false,
    error = null
  ) => (
    <div className='importWizardTableCell'>
      <span>
        {this.cellInput(
          data,
          rowIndex,
          columnName,
          isEditing == columnName,
          error
        )}
        {isEditing != columnName ? (
          <a
            onClick={() => this.setCellEdit(rowIndex, columnName)}
            className='btnLink'
          >
            <EditCircle />
          </a>
        ) : (
          <span>
            <a
              onClick={() => this.cancelCellEdit(rowIndex, columnName)}
              className='btnLink'
            >
              <CancelCircle />
            </a>
            <a
              onClick={() =>
                this.handleCellAdjust(
                  this.state.tableData[rowIndex],
                  rowIndex,
                  columnName
                )
              }
              className='btnLink'
            >
              <CheckOnCircle />
            </a>
          </span>
        )}
      </span>
      {error && <div className='cellErrors'>{error}</div>}
    </div>
  );

  adjustRow = (columns, data, rowIndex) => {
    const otherErrors = rowLevelErrors(data.errors, columns);
    return (
      <tr
        key={data.id}
        className={classnames({
          importWizardTableRow: true,
          importWizardRowProblem: data.errors?.length,
        })}
      >
        <td
          className={classnames({
            tableRowProblem: data.errors?.length,
            actionsColumn: true,
          })}
        >
          <a
            className='btnLink actions'
            title='Delete this row'
            onClick={() => this.handleDeleteRow(rowIndex, data.id)}
          >
            <CancelCircle />
          </a>
          <div className='rowLine'>Row {data.line}</div>
          {otherErrors.length > 0 && (
            <div className='cellErrors'>{otherErrors.join(' · ')}</div>
          )}
        </td>
        {columns.map((columnName) => {
          const errors = getCellErrors(data.errors, columnName);
          return (
            <td
              key={`${columnName}${rowIndex}`}
              className={classnames({ cellProblemWarning: errors })}
            >
              {this.adjustCell(
                data[columnName],
                rowIndex,
                columnName,
                data.isEditing,
                errors
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  // Next step: any column still unrecognised is ignored explicitly, then the preview shows what will happen.
  goToPreview = () => {
    const { unmatchedColumns, dataType, errorCount } = this.state;
    if (errorCount) return;
    if (unmatchedColumns.length) {
      this.ignoreColumns(unmatchedColumns).then((ok) => {
        if (ok) history.push(`/importWizard/preview/${dataType}`);
      });
    } else {
      history.push(`/importWizard/preview/${dataType}`);
    }
  };

  ignoreColumns = (names) => {
    const { dataType } = this.state;
    const query = names
      .map((name) => `column_names[]=${encodeURIComponent(name)}`)
      .join('&');
    return this.waitForRevalidation(
      service.delete(
        `${API}/api/columns?import_type=${dataType}&ignore=true&${query}`
      ),
      'Ignoring columns'
    );
  };

  handleDeleteRow = (rowIndex, rowId) => {
    if (this.state.totalRows <= 1) {
      this.setState({ lastRowDeleteConfirmation: true });
    } else {
      this.deleteRow(rowIndex, rowId);
    }
  };

  toggleLastRowDeleteModal = () => {
    this.setState((prevState) => ({
      lastRowDeleteConfirmation: !prevState.lastRowDeleteConfirmation,
    }));
  };

  deleteRow = (rowIndex, rowId) => {
    const { dataType } = this.state;
    service
      .delete(`${API}/api/import_data/${rowId}?import_type=${dataType}`)
      .then((response) => {
        this.setState({ errorCount: response.data.error_count });
        this.getOnGoingImport();
      })
      .catch((error) => {
        saveError(error);
        this.setState({ loading: false });
      });
  };

  cancelCurrentImport = () => {
    const { dataType } = this.state;
    this.props.openLoadingSpinner('Aborting Import');
    service
      .delete(`${API}/api/imports?import_type=${dataType}`)
      .then(() => {
        this.props.closeLoadingSpinner();
        this.redirectToSelectPage();
      })
      .catch((error) => {
        this.props.closeLoadingSpinner();
        saveError(error);
      });
  };

  toggleDeleteModal = (columnName = '') => {
    this.setState((prevState) => ({
      deleteColumnConfirmation: !prevState.deleteColumnConfirmation,
      columnToDelete: columnName,
    }));
  };

  handleColumnChange = (newColumn, oldColumn) => {
    const { dataType } = this.state;
    this.waitForRevalidation(
      service.patch(`${API}/api/columns`, {
        import_type: dataType,
        current_column: oldColumn,
        new_column: newColumn,
      }),
      'Switching column data'
    );
  };

  deleteColumn = () => {
    const { dataType, columnToDelete } = this.state;
    this.setState({ deleteColumnConfirmation: false });
    this.waitForRevalidation(
      service.delete(
        `${API}/api/columns?import_type=${dataType}&column_name=${encodeURIComponent(columnToDelete)}`
      ),
      'Deleting column'
    );
  };

  handleSearch = (searchQuery) => {
    const { searchTimeoutId } = this.state;
    if (searchTimeoutId) clearTimeout(searchTimeoutId);
    const timeoutId = setTimeout(
      () =>
        this.setState({ searchQuery, page: 1 }, () => this.getOnGoingImport()),
      600
    );
    this.setState({ searchTimeoutId: timeoutId });
  };

  handleFilterSubmit = (filterData) => {
    this.setState({ filterData, page: 1 }, () => this.getOnGoingImport());
    this.handleFilterDrawerOpen();
  };

  handlePerChange = (per) => {
    this.setState({ perPage: per, page: 1 }, () => this.getOnGoingImport());
  };

  handlePageChange = (data) => {
    this.setState({ page: data.selected + 1 }, () => this.getOnGoingImport());
  };

  handleFilterDrawerOpen = () => {
    this.setState((prevState) => ({
      isFilterDrawerOpen: !prevState.isFilterDrawerOpen,
    }));
  };

  toggleCancelModal = () => {
    this.setState((prevState) => ({
      openCancelConfirmation: !prevState.openCancelConfirmation,
    }));
  };

  redirectToSelectPage = () => history.push('/importWizard');

  unmatchedBanner = () => {
    const { unmatchedColumns, ignoredColumns } = this.state;
    if (!unmatchedColumns.length && !ignoredColumns.length) return null;
    return (
      <div className='importWizardUnmatched' id='import_wizard_unmatched'>
        {unmatchedColumns.length > 0 && (
          <p>
            <b>
              {unmatchedColumns.length}{' '}
              {unmatchedColumns.length === 1 ? 'column is' : 'columns are'} not
              recognised:
            </b>{' '}
            {unmatchedColumns.join(', ')}. Match each one to a column with its
            header dropdown, or{' '}
            <a
              className='btnLink'
              id='import_wizard_ignore_columns'
              onClick={() => this.ignoreColumns(unmatchedColumns)}
            >
              ignore these columns
            </a>
            .
          </p>
        )}
        {ignoredColumns.length > 0 && (
          <p>
            Ignored columns (kept in the file, not imported):{' '}
            {ignoredColumns.join(', ')}
          </p>
        )}
      </div>
    );
  };

  tableHeader = () => (
    <div className='filter'>
      <div className='importWizardTableFilterHeader'>
        <div>
          <input
            name='search'
            placeholder='Search'
            type='search'
            onChange={(e) => this.handleSearch(e.target.value)}
          />
        </div>
        <div>
          <button className='btnIcon' onClick={this.handleFilterDrawerOpen}>
            <FilterIcon />
            Filter
          </button>
          <select
            className='numberSelect'
            name='numberPer'
            defaultValue='25'
            onChange={(e) => this.handlePerChange(e.target.value)}
          >
            <option value='25'>25</option>
            <option value='50'>50</option>
          </select>
        </div>
      </div>
    </div>
  );

  adjustTable = () => {
    const { requiredColumns, displayColumns, tableData, loading } = this.state;
    return (
      <div>
        {this.tableHeader()}
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <table className='grid adjustTableImportWizard'>
              <tbody>
                <tr>
                  <th key='Actions'></th>
                  {displayColumns.map((columnName) => {
                    const isColumnMatched =
                      !this.state.unmatchedColumns.includes(columnName);
                    const dropdownColumns = requiredColumns.includes(columnName)
                      ? requiredColumns
                      : [...requiredColumns, 'Delete this column'];
                    return (
                      <th key={columnName}>
                        <Dropdown
                          className={classnames({
                            importColumnUnmatched: !isColumnMatched,
                            importColumnMatched: isColumnMatched,
                          })}
                          options={dropdownColumns}
                          placeholder={columnName}
                          value={columnName}
                          onChange={(e) => {
                            if (e.target.value === 'Delete this column') {
                              return this.toggleDeleteModal(columnName);
                            }
                            if (e.target.value !== columnName) {
                              this.handleColumnChange(
                                e.target.value,
                                columnName
                              );
                            }
                          }}
                        />
                      </th>
                    );
                  })}
                </tr>
                {tableData.map((rowData, rowIndex) =>
                  this.adjustRow(displayColumns, rowData, rowIndex)
                )}
              </tbody>
            </table>
            {Object.entries(this.state.columnOptions)
              .filter(([, options]) => options.multiple)
              .map(([column, options]) => (
                <datalist key={column} id={`options-${column}`}>
                  {options.values.map((value) => (
                    <option key={value} value={value} />
                  ))}
                </datalist>
              ))}
          </>
        )}
      </div>
    );
  };

  cancelModal = () => (
    <Modal
      open={this.state.openCancelConfirmation}
      hideCloseButton
      title='Cancel Import'
      actions={[
        <a key='no' className='btn full' onClick={this.toggleCancelModal}>
          No
        </a>,
        <a key='yes' className='btn full' onClick={this.cancelCurrentImport}>
          Yes
        </a>,
      ]}
    >
      <p>
        Are you sure you want to cancel the current import? This will delete all
        current import data.
      </p>
    </Modal>
  );

  deleteColumnModal = () => (
    <Modal
      open={this.state.deleteColumnConfirmation}
      hideCloseButton
      title='Are you sure you want to delete this column?'
      actions={[
        <a
          key='no'
          className='btn full'
          onClick={() => this.toggleDeleteModal()}
        >
          No
        </a>,
        <a key='yes' className='btn full' onClick={this.deleteColumn}>
          Yes
        </a>,
      ]}
    >
      <p>{this.state.columnToDelete}</p>
    </Modal>
  );

  lastRowDeleteModal = () => (
    <Modal
      open={this.state.lastRowDeleteConfirmation}
      hideCloseButton
      title='Import Cancel'
      actions={[
        <a
          key='no'
          className='btn full'
          onClick={this.toggleLastRowDeleteModal}
        >
          No
        </a>,
        <a key='yes' className='btn full' onClick={this.cancelCurrentImport}>
          Yes
        </a>,
      ]}
    >
      <p>
        Deleting the last row of this import sheet will cancel the import. Are
        you sure you want to continue?
      </p>
    </Modal>
  );

  render() {
    const {
      errorCount,
      totalPages,
      page,
      unmatchedColumns,
      displayColumns,
      isFilterDrawerOpen,
      totalRows,
    } = this.state;
    return (
      <div className='contentWrapper'>
        <ImportWizardHeader
          importStatus='adjust'
          onNextStep={this.goToPreview}
          displayButtons={true}
          onCancel={this.toggleCancelModal}
          onSaveForLater={this.redirectToSelectPage}
          disableFinalizeButton={errorCount > 0}
          headerTitle='Adjust Table'
        />
        <div className='ImportWizardAdjustContainer'>
          <div className='adjustTableMatches'>
            <span className='matches'>
              <b>
                {displayColumns.length - unmatchedColumns.length} /{' '}
                {displayColumns.length}
              </b>{' '}
              Matches
            </span>
            <span className='unmatched'>
              <b>
                {unmatchedColumns.length} / {displayColumns.length}
              </b>{' '}
              Unmatched
            </span>
            <span className='problems' id='import_wizard_problem_count'>
              <b>{errorCount}</b> Problems
            </span>
            <span className='rows'>
              <b>{totalRows}</b> Rows
            </span>
          </div>
          {this.unmatchedBanner()}
          {errorCount > 0 && (
            <p className='importWizardMessage warn'>
              {errorCount} {errorCount === 1 ? 'row has' : 'rows have'}{' '}
              problems. Fix the cells listed on each row or delete the row to
              continue.
            </p>
          )}
          {this.adjustTable()}
          <PaginationBoxView
            className='pagerWrapper'
            containerClassName='pager'
            pageCount={totalPages}
            onPageChange={this.handlePageChange}
            forcePage={page - 1}
          />
        </div>
        <footer className='importWizardSubmit'>
          <a
            id='import_wizard_next'
            className={classnames({
              btn: true,
              submitBtn: !errorCount,
              disabled: errorCount > 0,
            })}
            onClick={this.goToPreview}
          >
            {errorCount > 0
              ? `Fix ${errorCount} problem${errorCount === 1 ? '' : 's'} to continue`
              : 'Preview import'}
          </a>
          <a className='btn cancelBtn' onClick={this.toggleCancelModal}>
            Cancel
          </a>
        </footer>
        <ImportWizardAdjustFilterDrawer
          drawer={isFilterDrawerOpen}
          toggleDrawerOpen={this.handleFilterDrawerOpen}
          columns={displayColumns}
          handleSubmit={this.handleFilterSubmit}
        />
        {this.deleteColumnModal()}
        {this.cancelModal()}
        {this.lastRowDeleteModal()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ business: state.business.business });

export default withRouter(
  connect(mapStateToProps, actions)(ImportWizardAdjust)
);
